import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Trash, Plus, PencilSimple, FloppyDisk, X, Buildings, IdentificationCard, Users, ShieldCheck } from "@phosphor-icons/react";

/** Phase 6C — Master data + Role/Permission admin. Single tabbed page.
 *  Uses only backend endpoints (no hardcoded lists). Admin-only.
 */
const TABS = [
  { key: "departments", label: "Departments", icon: Buildings, api: "departments" },
  { key: "designations", label: "Designations", icon: IdentificationCard, api: "designations" },
  { key: "employment-statuses", label: "Employment Statuses", icon: Users, api: "employment-statuses" },
  { key: "office-locations", label: "Office Locations", icon: Buildings, api: "office-locations" },
  { key: "companies", label: "Companies", icon: Buildings, api: "companies" },
  { key: "service-types", label: "Service Types", icon: IdentificationCard, api: "service-types" },
  { key: "groups", label: "Groups", icon: Buildings, api: "groups" },
  { key: "roles", label: "Roles & Permissions", icon: ShieldCheck, api: "roles" },
  { key: "hierarchy-audit", label: "Hierarchy Audit", icon: ShieldCheck, api: "hierarchy-audit" },
];

function useMaster(apiKey) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/masters/${apiKey}${apiKey === "roles" ? "" : `?search=${encodeURIComponent(q)}&page_size=200`}`);
      setRows(apiKey === "roles" ? r.data : (r.data.rows || []));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, [apiKey, q]);
  useEffect(() => { load(); }, [load]);
  return { rows, loading, q, setQ, reload: load };
}

function SimpleMasterPanel({ tab }) {
  const { rows, loading, q, setQ, reload } = useMaster(tab.api);
  const [editing, setEditing] = useState(null);       // {id?, name, description}
  const isCreate = editing && !editing.id;

  const save = async () => {
    try {
      const payload = { name: editing.name, description: editing.description || null };
      if (isCreate) await api.post(`/masters/${tab.api}`, payload);
      else await api.patch(`/masters/${tab.api}/${editing.id}`, payload);
      toast.success("Saved");
      setEditing(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this record? It can be restored by admin.")) return;
    try { await api.delete(`/masters/${tab.api}/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input placeholder={`Search ${tab.label}…`} value={q} onChange={(e) => setQ(e.target.value)}
               data-testid={`master-${tab.key}-search`}
               className="border border-slate-200 rounded-md px-3 py-2 text-sm flex-1" />
        <button onClick={() => setEditing({ name: "", description: "" })}
                data-testid={`master-${tab.key}-add`}
                className="text-sm bg-slate-900 text-white rounded-md px-3 py-2 inline-flex items-center gap-1 hover:bg-slate-800">
          <Plus size={14} /> Add
        </button>
      </div>
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid={`master-${tab.key}-table`}>
          <thead className="bg-slate-50 text-slate-500">
            <tr><th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="px-3 py-2 w-24" /></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={3} className="text-center p-6 text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={3} className="text-center p-6 text-slate-400">No records</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`master-${tab.key}-row-${r.id}`}>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 text-slate-500">{r.description || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setEditing({ ...r })} className="text-slate-400 hover:text-slate-700 mr-2" data-testid={`master-${tab.key}-edit-${r.id}`}>
                    <PencilSimple size={14} />
                  </button>
                  <button onClick={() => remove(r.id)} className="text-slate-400 hover:text-red-600" data-testid={`master-${tab.key}-delete-${r.id}`}>
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50" role="dialog">
          <div className="bg-white rounded-lg p-6 w-[420px] space-y-3" data-testid={`master-${tab.key}-modal`}>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg">{isCreate ? "Add" : "Edit"} {tab.label.replace(/s$/, "")}</h3>
              <button onClick={() => setEditing(null)}><X size={16} /></button>
            </div>
            <label className="text-xs text-slate-500">Name *</label>
            <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                   data-testid="master-modal-name"
                   className="border border-slate-200 rounded-md px-3 py-2 text-sm w-full" />
            <label className="text-xs text-slate-500">Description</label>
            <textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                      data-testid="master-modal-description"
                      className="border border-slate-200 rounded-md px-3 py-2 text-sm w-full" />
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="px-3 py-2 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button onClick={save} disabled={!editing.name?.trim()}
                      data-testid="master-modal-save"
                      className="px-3 py-2 text-sm bg-slate-900 text-white rounded-md inline-flex items-center gap-1 disabled:opacity-50">
                <FloppyDisk size={14} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RolesPermissionsPanel() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState({ categories: [], permissions: [] });
  const [selectedRole, setSelectedRole] = useState(null);
  const [assigned, setAssigned] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([api.get("/masters/roles"), api.get("/masters/permissions")]);
      setRoles(r1.data);
      setCatalog(r2.data);
      setSelectedRole((prev) => prev || (r1.data.length ? r1.data[0].key : null));
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!selectedRole) return;
    api.get(`/masters/roles/${selectedRole}/permissions`)
      .then((r) => setAssigned(new Set(r.data)))
      .catch(() => setAssigned(new Set()));
  }, [selectedRole]);

  const toggle = (key) => {
    const s = new Set(assigned);
    s.has(key) ? s.delete(key) : s.add(key);
    setAssigned(s);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/masters/roles/${selectedRole}/permissions`, { permissions: [...assigned] });
      toast.success("Permissions saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const grouped = useMemo(() => {
    const g = {};
    (catalog.permissions || []).forEach((p) => {
      (g[p.category] ||= []).push(p);
    });
    return g;
  }, [catalog]);

  return (
    <div className="grid grid-cols-[220px_1fr] gap-4">
      <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="roles-list">
        <div className="text-xs uppercase text-slate-500 px-3 py-2 bg-slate-50">Roles</div>
        {roles.map((r) => (
          <button key={r.key} onClick={() => setSelectedRole(r.key)}
                  data-testid={`role-select-${r.key}`}
                  className={`w-full text-left px-3 py-2 text-sm border-t border-slate-100 hover:bg-slate-50 ${selectedRole === r.key ? "bg-sky-50 font-medium" : ""}`}>
            <div>{r.name}</div>
            <div className="text-[10px] text-slate-400">{r.key}{r.system ? " · system" : ""}</div>
          </button>
        ))}
      </div>
      <div>
        {!selectedRole ? <div className="text-sm text-slate-400 p-8">Select a role to edit permissions.</div> : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">{roles.find(r => r.key === selectedRole)?.name}</div>
                <div className="text-xs text-slate-500">{assigned.size} permissions assigned</div>
              </div>
              <button onClick={save} disabled={busy || selectedRole === "admin" || selectedRole === "super_admin"}
                      title={selectedRole === "admin" || selectedRole === "super_admin" ? "Admin/Super-Admin have full access" : ""}
                      data-testid="save-role-permissions"
                      className="bg-slate-900 text-white px-3 py-2 text-sm rounded-md inline-flex items-center gap-1 disabled:opacity-50">
                <FloppyDisk size={14} /> Save Permissions
              </button>
            </div>
            {Object.entries(grouped).map(([cat, perms]) => (
              <div key={cat} className="border border-slate-200 rounded-md" data-testid={`perm-cat-${cat}`}>
                <div className="text-xs uppercase text-slate-500 px-3 py-2 bg-slate-50">{cat}</div>
                <div className="grid grid-cols-3 gap-2 p-3">
                  {perms.map((p) => (
                    <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`perm-toggle-${p.key}`}>
                      <input type="checkbox" checked={assigned.has(p.key)} onChange={() => toggle(p.key)} />
                      <span>{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Masters() {
  const [tab, setTab] = useState(TABS[0]);
  return (
    <div className="space-y-5" data-testid="masters-page">
      <div>
        <h2 className="text-2xl font-semibold">Master Data</h2>
        <p className="text-sm text-slate-500">Departments, designations, employment statuses, office locations, companies, roles & permissions. All fully database-driven — no hardcoded values.</p>
      </div>
      <div className="flex gap-2 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t)}
                  data-testid={`masters-tab-${t.key}`}
                  className={`px-3 py-2 text-sm inline-flex items-center gap-1 border-b-2 ${tab.key === t.key ? "border-slate-900 font-medium" : "border-transparent text-slate-500"}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      {tab.key === "roles" ? <RolesPermissionsPanel />
        : tab.key === "hierarchy-audit" ? <HierarchyAuditPanel />
        : tab.key === "groups" ? <GroupsPanel />
        : tab.key === "service-types" ? <ServiceTypesPanel />
        : <SimpleMasterPanel tab={tab} />}
    </div>
  );
}

function GroupsPanel() {
  const [rows, setRows] = useState([]);
  const [teams, setTeams] = useState([]);
  const [editing, setEditing] = useState(null);   // group being edited, or {name:'',teams:[]} for new
  const load = useCallback(async () => {
    try {
      const r = await api.get("/masters/groups"); setRows(r.data.rows || []);
      const t = await api.get("/directory/employees?page_size=200");
      setTeams([...new Set((t.data.rows || []).map((u) => u.team).filter(Boolean))].sort());
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load groups"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!editing.name?.trim()) { toast.error("Name required"); return; }
    try {
      if (editing.id) await api.patch(`/masters/groups/${editing.id}`, { name: editing.name, description: editing.description, teams: editing.teams || [] });
      else            await api.post("/masters/groups",                 { name: editing.name, description: editing.description, teams: editing.teams || [] });
      toast.success(editing.id ? "Group updated" : "Group created");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const remove = async (g) => {
    if (g.employees_count > 0) { toast.error("Cannot delete: teams have employees assigned"); return; }
    if (!window.confirm(`Delete group '${g.name}'?`)) return;
    try { await api.delete(`/masters/groups/${g.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="space-y-4" data-testid="groups-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Groups</h3>
          <p className="text-xs text-slate-500">A Group is a named set of Teams. Managers see candidates from teams contained in their Managed Groups.</p>
        </div>
        <button onClick={() => setEditing({ name: "", description: "", teams: [] })} data-testid="group-create"
                className="text-sm bg-slate-900 text-white rounded-md px-3 py-2 hover:bg-slate-800">＋ New group</button>
      </div>
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Group</th>
              <th className="text-left px-3 py-2">Teams</th>
              <th className="text-left px-3 py-2">Employees</th>
              <th className="text-left px-3 py-2">Managers</th>
              <th className="text-left px-3 py-2">Group Leads</th>
              <th className="text-center px-3 py-2">Active</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((g) => (
              <tr key={g.id} data-testid={`group-row-${g.id}`} className="border-t border-slate-100">
                <td className="px-3 py-2">
                  <div className="font-medium">{g.name}</div>
                  {g.description && <div className="text-xs text-slate-500">{g.description}</div>}
                </td>
                <td className="px-3 py-2 text-slate-600">
                  <div className="flex flex-wrap gap-1">
                    {(g.teams || []).map((t) => (
                      <span key={t} className="text-xs bg-slate-100 rounded-full px-2 py-0.5">{t}</span>
                    ))}
                    {(!g.teams || g.teams.length === 0) && <span className="text-xs text-slate-400">— none —</span>}
                  </div>
                </td>
                <td className="px-3 py-2">{g.employees_count}</td>
                <td className="px-3 py-2">{g.managers_count}</td>
                <td className="px-3 py-2">{g.group_leads_count}</td>
                <td className="px-3 py-2 text-center">
                  <label className="inline-flex items-center gap-2 cursor-pointer" title={g.active ? "Click to deactivate" : "Click to activate"}>
                    <input type="checkbox" className="peer sr-only"
                           data-testid={`group-toggle-${g.id}`}
                           checked={!!g.active}
                           onChange={async () => { try { await api.patch(`/masters/groups/${g.id}/toggle-active`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Toggle failed"); } }} />
                    <span className="w-9 h-5 rounded-full bg-slate-300 peer-checked:bg-emerald-500 relative transition-colors">
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${g.active ? "left-[18px]" : "left-0.5"}`}></span>
                    </span>
                    <span className={`text-xs ${g.active ? "text-emerald-700" : "text-slate-500"}`}>{g.active ? "Active" : "Inactive"}</span>
                  </label>
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setEditing({ ...g })} data-testid={`group-edit-${g.id}`}
                          className="text-sm text-sky-600 hover:text-sky-800 mr-2">Edit</button>
                  <button onClick={() => remove(g)} data-testid={`group-delete-${g.id}`}
                          className="text-sm text-red-600 hover:text-red-800" disabled={g.employees_count > 0 || g.managers_count > 0}>Delete</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No groups yet. Create one above.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="fixed inset-0 bg-slate-900/40 grid place-items-center z-50" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg shadow-lg p-5 w-full max-w-lg space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold">{editing.id ? "Edit group" : "New group"}</h3>
            <input className="input" placeholder="Name" data-testid="group-name-input"
                   value={editing.name || ""} onChange={(e) => setEditing((g) => ({ ...g, name: e.target.value }))} />
            <input className="input" placeholder="Description (optional)" data-testid="group-desc-input"
                   value={editing.description || ""} onChange={(e) => setEditing((g) => ({ ...g, description: e.target.value }))} />
            <div>
              <div className="text-xs uppercase text-slate-500 mb-1">Teams in this group</div>
              <div className="border border-slate-200 rounded-md p-2 flex flex-wrap gap-1" data-testid="group-teams-picker">
                {(editing.teams || []).map((t) => (
                  <span key={t} className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full px-2 py-0.5 flex items-center gap-1">
                    {t}
                    <button onClick={() => setEditing((g) => ({ ...g, teams: (g.teams || []).filter((x) => x !== t) }))} className="hover:text-red-600">×</button>
                  </span>
                ))}
                <select className="input text-sm" value="" onChange={(e) => { if (e.target.value) setEditing((g) => ({ ...g, teams: [...new Set([...(g.teams || []), e.target.value])] })); }}>
                  <option value="">＋ Add team…</option>
                  {teams.filter((t) => !(editing.teams || []).includes(t)).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="text-sm px-3 py-1.5 border border-slate-200 rounded-md">Cancel</button>
              <button onClick={save} data-testid="group-save" className="text-sm bg-sky-600 text-white px-3 py-1.5 rounded-md hover:bg-sky-700">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* UAT Batch 1 — Service Types master with default_duration_days */
function ServiceTypesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const isCreate = editing && !editing.id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/masters/service-types?search=${encodeURIComponent(q)}&page_size=200`);
      setRows(r.data.rows || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = {
        name: editing.name,
        default_duration_days: Number(editing.default_duration_days) || 30,
        description: editing.description || null,
      };
      if (isCreate) await api.post("/masters/service-types", payload);
      else await api.patch(`/masters/service-types/${editing.id}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Soft-delete this Service Type? Existing candidates referencing it are untouched.")) return;
    try { await api.delete(`/masters/service-types/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-3" data-testid="service-types-panel">
      <div className="flex items-center gap-2">
        <input placeholder="Search Service Types…" value={q} onChange={(e) => setQ(e.target.value)}
               data-testid="master-service-types-search"
               className="border border-slate-200 rounded-md px-3 py-2 text-sm flex-1" />
        <button onClick={() => setEditing({ name: "", default_duration_days: 30, description: "" })}
                data-testid="master-service-types-add"
                className="text-sm bg-slate-900 text-white rounded-md px-3 py-2 inline-flex items-center gap-1 hover:bg-slate-800">
          <Plus size={14} /> Add
        </button>
      </div>
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="master-service-types-table">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Default Duration (days)</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="px-3 py-2 w-24" />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="text-center p-6 text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={4} className="text-center p-6 text-slate-400">No records</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`master-service-types-row-${r.id}`}>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 tabular-nums">{r.default_duration_days}</td>
                <td className="px-3 py-2 text-slate-500">{r.description || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setEditing({ ...r })} className="text-slate-400 hover:text-slate-700 mr-2" data-testid={`master-service-types-edit-${r.id}`}>
                    <PencilSimple size={14} />
                  </button>
                  <button onClick={() => remove(r.id)} className="text-slate-400 hover:text-red-600" data-testid={`master-service-types-delete-${r.id}`}>
                    <Trash size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50" role="dialog">
          <div className="bg-white rounded-lg p-6 w-[440px] space-y-3" data-testid="master-service-types-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg">{isCreate ? "Add" : "Edit"} Service Type</h3>
              <button onClick={() => setEditing(null)}><X size={16} /></button>
            </div>
            <label className="text-xs text-slate-500">Name *</label>
            <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                   data-testid="service-type-modal-name"
                   className="border border-slate-200 rounded-md px-3 py-2 text-sm w-full" />
            <label className="text-xs text-slate-500">Default Duration (days) *</label>
            <input type="number" min={1} max={3650}
                   value={editing.default_duration_days ?? 30}
                   onChange={(e) => setEditing({ ...editing, default_duration_days: e.target.value })}
                   data-testid="service-type-modal-duration"
                   className="border border-slate-200 rounded-md px-3 py-2 text-sm w-full" />
            <p className="text-[11px] text-slate-500">
              Determines the default program length applied to candidates whose Service Type = this. Admin can override on individual candidates.
            </p>
            <label className="text-xs text-slate-500">Description</label>
            <textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                      data-testid="service-type-modal-description"
                      className="border border-slate-200 rounded-md px-3 py-2 text-sm w-full" />
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="px-3 py-2 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button onClick={save} disabled={!editing.name?.trim() || !editing.default_duration_days}
                      data-testid="service-type-modal-save"
                      className="px-3 py-2 text-sm bg-slate-900 text-white rounded-md inline-flex items-center gap-1 disabled:opacity-50">
                <FloppyDisk size={14} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function HierarchyAuditPanel() {
  const [state, setState] = useState({ loading: true, data: null });
  const load = useCallback(async () => {
    setState({ loading: true, data: null });
    try {
      const r = await api.get("/masters/hierarchy-audit");
      setState({ loading: false, data: r.data });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load audit"); setState({ loading: false, data: null }); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const d = state.data;
  const KIND_LABELS = {
    wrong_parent_role: "Wrong parent role",
    manager_missing: "Missing / deleted reporting manager",
    circular: "Circular reporting chain",
    self_manager: "Self-referential manager",
  };
  return (
    <div className="space-y-4" data-testid="hierarchy-audit-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Reporting hierarchy audit</h3>
          <p className="text-xs text-slate-500">Scans every active user and reports invalid reporting relationships. Fix these before they impact visibility, reports, or dashboards.</p>
        </div>
        <button onClick={load} data-testid="hierarchy-audit-refresh"
                className="text-sm bg-slate-900 text-white rounded-md px-3 py-2 hover:bg-slate-800">Rescan</button>
      </div>
      {state.loading && <div className="text-sm text-slate-400">Scanning…</div>}
      {d && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hierarchy-audit-summary">
            <div className="bg-white border border-slate-200 rounded-md p-3">
              <div className="text-xs uppercase text-slate-500">Users scanned</div>
              <div className="text-2xl font-semibold text-slate-900" data-testid="hier-scanned">{d.users_scanned}</div>
            </div>
            <div className={`border rounded-md p-3 ${d.total === 0 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
              <div className="text-xs uppercase text-slate-500">Total issues</div>
              <div className={`text-2xl font-semibold ${d.total === 0 ? "text-emerald-700" : "text-amber-700"}`} data-testid="hier-total">{d.total}</div>
            </div>
            {Object.entries(d.counts_by_kind || {}).map(([k, n]) => (
              <div key={k} className="bg-white border border-slate-200 rounded-md p-3">
                <div className="text-xs uppercase text-slate-500">{KIND_LABELS[k] || k}</div>
                <div className="text-2xl font-semibold text-slate-900" data-testid={`hier-count-${k}`}>{n}</div>
              </div>
            ))}
          </div>
          {d.total === 0 ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-sm text-emerald-800" data-testid="hier-clean">
              ✅ Every active user has a valid reporting relationship. No action needed.
            </div>
          ) : (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="hier-issues-table">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Employee</th>
                    <th className="text-left px-3 py-2">Role</th>
                    <th className="text-left px-3 py-2">Issue</th>
                    <th className="text-left px-3 py-2">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {d.issues.map((it) => (
                    <tr key={it.user_id} className="border-t border-slate-100" data-testid={`hier-issue-${it.user_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{it.name}</div>
                        <div className="text-xs text-slate-500">{it.email}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{it.role}</td>
                      <td className="px-3 py-2">
                        <span className="text-xs bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">{KIND_LABELS[it.kind] || it.kind}</span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{it.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
